// 符號 Spine 骨骼載入與工廠 — 沿用 Unity 來源 Art/Animaton 的 Spine 4.2.43 資產（見 docs 掃描紀錄）。
// 骨骼為二進位 .skel、圖集 .atlas（pma:true 預乘 alpha，spine-pixi 4.2 自動處理），已複製至 public/spine/。
// 動畫名稱皆實測自來源骨骼與 SymbolView.cs 的命名規則（動畫名由 pickSymbolAnimation 依 base 名組出）：
//   - 落定 land：只有 WW(land_1x{n})、H1(land_1x{n})、主遊戲 SS(SS_land_1x{n}) 有；其餘符號無、落定不演出。
//   - 待機 loop：只有 WW(loop_1x{n})、主遊戲 SS(SS_loop_1x{n}) 有（H1 只有 land，播完即回靜態貼圖）。
//   - 中獎/消除：WW/H1~H4 用 win_1x{n}、H5~H7/LA~LJ 用裸名 win、SS 用 SS_win_1x{n}／FG 用 S{Level}_win_1x{n}。
//   - 消除時另疊加共用的 ani_remove 骨骼（remove_1x{n}）當爆散特效。
//   - 實測 land 與 loop 每一支長度皆恰為 1.0000s，與 Unity 硬編的 1.0f 完全吻合（見 constants.SYMBOL_STATE_ANIM_MS）。

import { Assets } from 'pixi.js'
import { Spine } from '@esotericsoftware/spine-pixi-v8'
import { isSpinePrimed, primeSpineAssets } from '../../framework/assets/spineBundle'

export interface SymbolSpineDef {
  /** 骨骼 .skel 路徑（同時作為 Assets alias）。 */
  readonly skel: string
  /** 圖集 .atlas 路徑（同時作為 Assets alias；H5~H7、LA~LJ 各自共用一份）。 */
  readonly atlas: string
  /** 中獎/消除動畫名（僅供文件對照；實際播放名一律由 pickSymbolAnimation 依骨骼實有動畫決定）。 */
  readonly win: string
}

const BASE = '/spine'

/** 符號 id → Spine 定義（見檔頭說明）。 */
const DEFS: Readonly<Record<number, SymbolSpineDef>> = {
  0: { skel: `${BASE}/WW/ani_WW.skel`, atlas: `${BASE}/WW/ani_WW.atlas`, win: 'win_1x1' },
  1: { skel: `${BASE}/H1/ani_H1.skel`, atlas: `${BASE}/H1/ani_H1.atlas`, win: 'win_1x1' },
  2: { skel: `${BASE}/H2/ani_H2.skel`, atlas: `${BASE}/H2/ani_H2.atlas`, win: 'win_1x1' },
  3: { skel: `${BASE}/H3/ani_H3.skel`, atlas: `${BASE}/H3/ani_H3.atlas`, win: 'win_1x1' },
  4: { skel: `${BASE}/H4/ani_H4.skel`, atlas: `${BASE}/H4/ani_H4.atlas`, win: 'win_1x1' },
  5: { skel: `${BASE}/H567/ani_H5.skel`, atlas: `${BASE}/H567/ani_symbol.atlas`, win: 'win' },
  6: { skel: `${BASE}/H567/ani_H6.skel`, atlas: `${BASE}/H567/ani_symbol.atlas`, win: 'win' },
  7: { skel: `${BASE}/H567/ani_H7.skel`, atlas: `${BASE}/H567/ani_symbol.atlas`, win: 'win' },
  8: { skel: `${BASE}/L/ani_LA.skel`, atlas: `${BASE}/L/ani_symbol.atlas`, win: 'win' },
  9: { skel: `${BASE}/L/ani_LK.skel`, atlas: `${BASE}/L/ani_symbol.atlas`, win: 'win' },
  10: { skel: `${BASE}/L/ani_LQ.skel`, atlas: `${BASE}/L/ani_symbol.atlas`, win: 'win' },
  11: { skel: `${BASE}/L/ani_LJ.skel`, atlas: `${BASE}/L/ani_symbol.atlas`, win: 'win' },
  12: { skel: `${BASE}/SS/ani_SS.skel`, atlas: `${BASE}/SS/ani_SS.atlas`, win: 'SS_win_1x1' },
}

/** 共用消除爆散特效骨骼。 */
const REMOVE_SKEL = `${BASE}/remove/ani_remove.skel`
const REMOVE_ATLAS = `${BASE}/remove/ani_remove.atlas`
/** 消除特效動畫名（本專案符號 1 格高）。 */
export const REMOVE_ANIMATION = 'remove_1x1'

/** 依長條高度（1~4）取得對應的消除爆散動畫名（remove_1x1 ~ remove_1x4）。 */
export function removeAnimationName(size: number): string {
  const clamped = Math.max(1, Math.min(4, Math.round(size)))
  return `remove_1x${clamped}`
}

/** Scatter 符號 id（避免循環依賴不 import constants；與 SymbolData.asset 的 12=SS 一致）。 */
const SS_ID = 12

/**
 * 依 Unity SymbolView.GetAnimationName 規則挑選符號動畫名：
 *   - Scatter(id12)：FG（scatterLevel>0）用 `S{Level}_{base}`、主遊戲用 `SS_{base}`；
 *   - 一般符號：優先裸名 `{base}`，骨骼沒有時補 `_1x{span}` 後綴。
 * 依序嘗試候選名，回傳骨骼實際存在的第一個；全部不存在回 null（呼叫端走 fallback 演出）。
 */
export function pickSymbolAnimation(
  spine: Spine,
  base: string,
  symbolId: number,
  span: number,
  scatterLevel: number,
): string | null {
  const clampedSpan = Math.max(1, Math.min(4, Math.round(span)))
  const candidates: string[] = []
  if (symbolId === SS_ID) {
    // Scatter 的兩個前綴在 Unity 是「互斥」的（GetAnimationName 依 IsFreeGame 二選一，不互相 fallback）：
    // FG 野狼骨骼只有 S{Level}_win_1x{n}、沒有 S{Level}_land/loop，若讓它退回 SS_ 前綴就會演出
    // 主遊戲的警示牌造型、與畫面上的野狼臉靜態貼圖不符。故此處只推入當前模式對應的前綴。
    const prefix = scatterLevel > 0 ? `S${scatterLevel}` : 'SS'
    candidates.push(`${prefix}_${base}`, `${prefix}_${base}_1x${clampedSpan}`)
  }
  candidates.push(base, `${base}_1x${clampedSpan}`)
  for (const name of candidates) {
    if (spine.skeleton.data.findAnimation(name) !== null) return name
  }
  return null
}

let loaded = false

/** 載入全部符號骨骼 + 圖集 + 共用消除骨骼。第一次畫面前呼叫（見 main.ts）。 */
export async function loadSymbolSpines(): Promise<void> {
  if (loaded) return

  const defs = Object.keys(DEFS).map((key) => DEFS[Number(key)]!)
  defs.push({ skel: REMOVE_SKEL, atlas: REMOVE_ATLAS, win: '' })
  // 優先由 spine bundle 供應（0 筆散檔請求）；bundle 缺失/過期時才逐檔載入。
  await primeSpineAssets(defs)

  // 去重（H5~H7、LA~LJ 共用圖集；每個 skel/atlas 只註冊一次），alias 直接用路徑。
  const srcs = new Set<string>()
  for (const def of defs) {
    if (isSpinePrimed(def)) continue
    srcs.add(def.skel)
    srcs.add(def.atlas)
  }

  for (const src of srcs) Assets.add({ alias: src, src })
  if (srcs.size > 0) await Assets.load([...srcs])
  loaded = true
}

/** 依符號 id 建立一個 Spine 顯示物件（共用已載入的骨骼資料/圖集）。未定義則回傳 null。 */
export function createSymbolSpine(symbolId: number): Spine | null {
  const def = DEFS[symbolId]
  if (def === undefined) return null
  return Spine.from({ skeleton: def.skel, atlas: def.atlas })
}

/** 建立一個共用消除爆散特效的 Spine 顯示物件。 */
export function createRemoveSpine(): Spine {
  return Spine.from({ skeleton: REMOVE_SKEL, atlas: REMOVE_ATLAS })
}
